import React from "react";
import { Dialog } from "components/main/utils/Dialog";
import {
  EnterpriseCard,
  PlanCard,
  PricingPageLink,
} from "components/main/organizations/PlanCard";
import type { CatalogPlan } from "@/lib/api/organizations";
import type { BillingInterval } from "@/lib/paddle";

interface PlanChoice {
  tier: CatalogPlan;
  state: "current" | "upgrade";
}

/// Presentational: figures, term and seat count are the panel's state.
export const PlansDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  plans: PlanChoice[];
  interval: BillingInterval;
  onIntervalChange: (interval: BillingInterval) => void;
  offersYearly?: (code: string) => boolean;
  seats: number;
  /// Members plus outstanding invitations — the stepper's floor.
  minSeats?: number;
  /// Absent where the count is not the buyer's to set (a personal org).
  onSeatsChange?: (seats: number) => void;
  livePrices: Record<string, string>;
  canBuy: (code: string) => boolean;
  onSubscribe: (code: string) => void;
  busyCode: string | null;
  error?: string | null;
  onContactSales: (anchorTop?: number) => void;
}> = ({
  open,
  onClose,
  plans,
  interval,
  onIntervalChange,
  offersYearly = () => false,
  seats,
  minSeats,
  onSeatsChange,
  livePrices,
  canBuy,
  onSubscribe,
  busyCode,
  error,
  onContactSales,
}) => {
  // The inheritance line names the priciest tier, so a rename cannot strand it.
  const topPaidName = plans
    .filter(({ tier }) => tier.price_cents > 0)
    .sort((a, b) => b.tier.price_cents - a.tier.price_cents)[0]?.tier.name;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="wide"
      title="Plans"
      autoSpacing={false}
    >
      {/* The wide dialog pads its title but not its body. */}
      <div className="flex flex-col gap-4 px-5 py-3">
        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(15rem, 1fr))",
          }}
        >
          {plans.map(({ tier, state }) => (
            <PlanCard
              key={tier.code}
              tier={tier}
              state={state}
              seats={seats}
              minSeats={minSeats}
              livePrice={livePrices[tier.code]}
              interval={interval}
              // Same predicate as the CTA: the seat count is shared across cards.
              onSeatsChange={
                state === "upgrade" && canBuy(tier.code)
                  ? onSeatsChange
                  : undefined
              }
              onSubscribe={
                state === "upgrade" && canBuy(tier.code)
                  ? () => onSubscribe(tier.code)
                  : undefined
              }
              busy={busyCode === tier.code}
              onIntervalChange={
                state === "upgrade" &&
                canBuy(tier.code) &&
                offersYearly(tier.code)
                  ? onIntervalChange
                  : undefined
              }
            />
          ))}
          <EnterpriseCard
            above={topPaidName}
            // Closed first: the support panel opens against the sidebar, under a modal.
            onContactSales={(anchorTop) => {
              onClose();
              onContactSales(anchorTop);
            }}
          />
        </div>

        <span className="flex items-center justify-between gap-3">
          <PricingPageLink />
          {error && <span className="text-error-regular">{error}</span>}
        </span>
      </div>
    </Dialog>
  );
};
